import { SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, map, startWith } from 'rxjs';

import { AuthService } from 'src/app/auth.service';
import { RoleOption, RolePickerComponent } from 'src/app/shared/components/role-picker/role-picker.component';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { environment } from 'src/environments/environment';
import { AddGroupMemberComponent } from '../add-group-member/add-group-member.component';
import { GroupFormComponent } from '../group-form/group-form.component';
import { GroupsService } from '../groups.service';

// --- Interfaces for type safety ---
interface GroupEdition {
  language: string;
  content: string;
}

interface GroupMember {
  _id: string;
  role: string;
}

// Raw group structure from the service
interface RawGroup {
  _id: string;
  name?: string;
  __name?: { editions: GroupEdition[] };
  __description?: { editions: GroupEdition[] };
  members: GroupMember[];
  people: RawUser[]; // Array of user objects
}

// Raw user structure from the service
interface RawUser {
  _id: string;
  name: string;
  email: string;
  photo?: string;
}

// Processed group data for the first table
interface ProcessedGroup {
  _id: string;
  actualName: string;
  actualDescription: string;
  userRole: string;
  memberCount: number;
}

// Processed user data for the second table
interface ProcessedUser {
  id: string;
  photo: string;
  name: string;
  email: string;
  type: string; // 'Admin', 'Writer', 'Reader'
}

@Component({
  selector: 'app-group-overview',
  imports: [
    SlicePipe,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatTableModule,
    MatTooltipModule,
    TranslateModule,
    RolePickerComponent,
  ],
  templateUrl: './group-overview.component.html',
  styleUrls: ['./group-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupOverviewComponent implements OnInit, OnDestroy {
  public sidenavService = inject(SidenavService);
  public isSmallScreen = this.sidenavService.isSmallOrTablet;

  private toggleEffect = effect(() => {
    const isSmall = this.isSmallScreen();
    this.sidenavService.showGlobalToggle.set(!isSmall);
  });

  // --- Services ---
  private groupsService = inject(GroupsService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private dialog = inject(MatDialog);
  private storage = inject(Storage);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private confirmMenuService = inject(ConfirmMenuService);
  private toast = inject(ToastService);
  private breakpointObserver = inject(BreakpointObserver);
  private cdr = inject(ChangeDetectorRef);

  showTiles = toSignal(
    this.breakpointObserver.observe('(max-width: 960px)').pipe(map(r => r.matches)),
    { initialValue: false }
  );

  // --- State Signals ---
  viewState = signal<'GROUPS' | 'USERS'>('GROUPS');
  loading = signal(false);
  searchTerm = signal('');

  // --- Data Signals ---
  private groupsResult = signal<RawGroup[]>([]);
  private usersResult = signal<ProcessedUser[]>([]);
  selectedGroup = signal<ProcessedGroup | null>(null);

  private routeParams = toSignal(this.route.params);
  private currentUserId = this.authService.getCurrentUser().id;
  private langChange = toSignal(this.translate.onLangChange.pipe(startWith(null)));
  private groupEvent = toSignal(this.groupsService.groupEvent$.pipe(startWith(null)));

  groupDisplayedColumns = ['name', 'description', 'role', 'members', 'actions'];
  userDisplayedColumns = ['photo', 'name', 'email', 'type', 'actions'];

  roleOptions: RoleOption[] = [
    { value: 'Admin', label: 'GroupOverview.Role.Admin' },
    { value: 'Writer', label: 'GroupOverview.Role.Writer' },
    { value: 'Reader', label: 'GroupOverview.Role.Reader' },
  ];

  constructor() {
    // This effect reacts to language changes, route parameter changes, and group events.
    effect(() => {
      // Reruns when any of these signals change
      this.langChange();
      this.groupEvent();
      const params = this.routeParams();
      const groupId = params?.['id'];

      if (groupId) {
        this.viewState.set('USERS');
        this.fetchUsersForGroup(groupId);
        // We need to ensure groups are loaded to find the selected one by name
        if (this.groupsResult().length === 0) {
          this.fetchGroups(groupId);
        } else {
          this.updateSelectedGroup(groupId);
        }
      } else {
        this.viewState.set('GROUPS');
        this.selectedGroup.set(null);
        this.usersResult.set([]);
        this.fetchGroups();
      }
    });
  }

  onRoleChange(member: ProcessedUser, newRole: string): void {
    const group = this.selectedGroup();
    if (!group) return;

    // Check for the last admin rule
    const admins = this.usersResult().filter((user) => user.type === 'Admin');
    if (member.type === 'Admin' && admins.length <= 1 && newRole !== 'Admin') {
      this.toast.snackbar.open(this.translate.instant('Snackbar.GroupMustHaveAdmin'), 'Ok', { duration: 4000 });
      // Revert the change in the UI
      setTimeout(() => this.fetchUsersForGroup(group._id), 100);
      return;
    }

    this.groupsService
      .editEntity({
        groupID: group._id,
        entityID: member.id,
        entityRole: newRole,
      })
      .subscribe(
        () => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.GroupMemberEditSuccess'), 'Ok', { duration: 2000 });
          this.fetchUsersForGroup(group._id);
        },
        () => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
        }
      );
  }

  ngOnInit() {
    // Initial data fetch is now handled by the effect in the constructor
  }

  fetchGroups(selectedGroupId?: string) {
    this.loading.set(true);
    this.groupsService
      .getGroups()
      .pipe(
        catchError(() => {
          this.loading.set(false);
          return EMPTY;
        })
      )
      .subscribe((groups) => {
        this.groupsResult.set(groups as RawGroup[]);
        if (selectedGroupId) {
          this.updateSelectedGroup(selectedGroupId);
        }
        this.loading.set(false);
        this.cdr.markForCheck();
      });
  }

  // Helper to find and set the selected group from the main list
  private updateSelectedGroup(groupId: string) {
    const group = this.processedGroups().find((g) => g._id === groupId);
    this.selectedGroup.set(group || null);
  }

  async fetchUsersForGroup(groupId: string) {
    this.loading.set(true);
    this.usersResult.set([]);
    try {
      const group = (await this.groupsService.getGroup(groupId).toPromise()) as RawGroup;

      const processedUsers: ProcessedUser[] = [];
      if (group && group.people) {
        for (const user of group.people) {
          const memberInfo = group.members.find((m) => m._id === user._id);
          const photoUrl = user.photo ? await this.getDownloadURL(user.photo) : 'assets/images/no_user.png';

          processedUsers.push({
            id: user._id,
            name: user.name,
            email: user.email,
            photo: photoUrl,
            type: memberInfo ? memberInfo.role : 'N/A',
          });
        }
      }
      this.usersResult.set(processedUsers);
    } catch (error) {
      console.error('Error fetching group details:', error);
    } finally {
      this.loading.set(false);
      this.cdr.markForCheck();
    }
  }

  private async getDownloadURL(url: string): Promise<string> {
    try {
      const path = url.startsWith('gs://') ? url.split(`/${environment.profilePictureBucketName}/`)[1] : url;
      const reference = ref(this.storage, `gs://${environment.profilePictureBucketName}/${path}`);
      return await getDownloadURL(reference);
    } catch (error) {
      return 'assets/images/no_user.png';
    }
  }

  processedGroups = computed<ProcessedGroup[]>(() => {
    const lang = this.langChange()?.lang || this.translate.currentLang;
    return this.groupsResult().map((group) => {
      const userRoleInfo = group.members.find((m) => m._id === this.currentUserId);
      const userRole = userRoleInfo ? userRoleInfo.role : 'N/A';

      return {
        _id: group._id,
        actualName: this.getTranslatedContent(group.__name, lang) || group.name || '',
        actualDescription: this.getTranslatedContent(group.__description, lang) || '',
        userRole: userRole,
        memberCount: group.members.length,
      };
    });
  });

  filteredGroups = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term) {
      return this.processedGroups();
    }
    return this.processedGroups().filter((group) => group.actualName.toLowerCase().includes(term) || group.actualDescription.toLowerCase().includes(term));
  });

  filteredUsers = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term) {
      return this.usersResult();
    }
    return this.usersResult().filter((user) => user.name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term));
  });

  private getTranslatedContent(translatable: { editions: GroupEdition[] } | undefined, lang: string): string | null {
    if (!translatable || !translatable.editions) return null;
    const edition = translatable.editions.find((e) => e.language === lang);
    return edition ? edition.content : translatable.editions[0]?.content || null;
  }

  onSearchChange(value: string) {
    this.searchTerm.set(value);
  }

  onGroupClicked(group: ProcessedGroup) {
    this.router.navigate(['/groups', group._id]);
  }

  onBackToGroups() {
    this.router.navigate(['/groups']);
  }

  /** Opens the group creation dialog */
  onCreateGroup() {
    this.dialog.open(GroupFormComponent, {
      width: '500px',
      data: {
        action: 'create',
      },
    }).afterClosed().subscribe((res) => {
      if (res) {
        this.fetchGroups();
      }
    });
  }

  /** Opens the group editing dialog */
  onEditGroup(group: ProcessedGroup, event: MouseEvent) {
    event.stopPropagation();
    if (!group) return;

    this.dialog.open(GroupFormComponent, {
      width: '500px',
      data: {
        action: 'edit',
        group: group,
      },
    }).afterClosed().subscribe((res) => {
      if (res) {
        this.fetchGroups();
      }
    });
  }

  onAddMember() {
    const group = this.selectedGroup();
    if (!group) return;

    const existingMemberIds = this.usersResult().map((u) => u.id);

    const dialogRef = this.dialog.open(AddGroupMemberComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        existingMemberIds,
      },
    });

    dialogRef.afterClosed().subscribe((userIds: string[]) => {
      if (userIds && userIds.length > 0) {
        this.groupsService
          .addEntity({
            groupID: group._id,
            entityIDs: userIds,
            entityRole: 'Reader', // Default role
          })
          .subscribe(() => {
            this.fetchUsersForGroup(group._id);
          });
      }
    });
  }

  /** Delete a group with popover confirmation */
  onDeleteGroup(group: ProcessedGroup, event: MouseEvent) {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    this.confirmMenuService.confirm(target, this.translate.instant('Global.Delete'), 'delete_outline', '#ef4444').subscribe(() => {
      this.groupsService.deleteGroup(group._id).subscribe(() => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
        this.fetchGroups();
      });
    });
  }

  /** Delete a member with popover confirmation */
  onDeleteMember(memberToDelete: ProcessedUser, event: MouseEvent): void {
    const group = this.selectedGroup();
    if (!group) return;

    // Check for the last admin rule
    const admins = this.usersResult().filter((user) => user.type === 'Admin');
    if (memberToDelete.type === 'Admin' && admins.length <= 1) {
      this.toast.snackbar.open(this.translate.instant('Snackbar.GroupMustHaveAdmin'), 'Ok', { duration: 4000 });
      return;
    }

    const target = event.currentTarget as HTMLElement;
    this.confirmMenuService.confirm(target, this.translate.instant('Global.Delete'), 'delete_outline', '#ef4444').subscribe(() => {
      this.groupsService
        .deleteMember({
          groupID: group._id,
          entityID: memberToDelete.id,
        })
        .subscribe(
          () => {
            this.toast.snackbar.open(this.translate.instant('Snackbar.GroupMemberRemovalSuccess'), 'Ok', { duration: 2000 });
            this.fetchUsersForGroup(group._id);
          },
          () => {
            this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
          }
        );
    });
  }

  ngOnDestroy(): void {
    this.sidenavService.showGlobalToggle.set(true);
  }
}
